/**
 * Trip Checklist Component
 *
 * Interactive checklist for trip preparation with localStorage persistence.
 * Tracks customer progress through pre-trip tasks.
 *
 * Epic: MVP 1.21.5 - Dashboard Completion
 * Story: 4.1 - Trip Preparation Checklist
 *
 * @version 1.0
 * @created 2026-01-17
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Calendar, MapPin as _MapPin, Users as _Users, CreditCard, Cloud } from 'lucide-react';

const CHECKLIST_ITEMS = [
  {
    id: 'pack_gear',
    label: 'Pack Required Gear',
    description: 'Review the gear list and pack everything you need',
    icon: Calendar
  },
  {
    id: 'save_directions',
    label: 'Save Meeting Location',
    description: 'Save the meeting location to your GPS or maps app',
    icon: _MapPin
  },
  {
    id: 'share_contacts',
    label: 'Share Trip Details',
    description: 'Share trip details with family or emergency contact',
    icon: _Users
  },
  {
    id: 'confirm_payment',
    label: 'Confirm Payment',
    description: 'Ensure your payment method is up to date',
    icon: CreditCard
  },
  {
    id: 'check_weather',
    label: 'Check Weather Forecast',
    description: 'Review weather conditions and pack accordingly',
    icon: Cloud
  }
];

export default function TripChecklist({ bookingId }) {
  const [checkedItems, setCheckedItems] = useState({});

  // Load checklist state from localStorage on mount
  useEffect(() => {
    if (!bookingId) {return;}

    const storageKey = `trip-checklist-${bookingId}`;
    const saved = localStorage.getItem(storageKey);

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setCheckedItems(parsed);
      } catch (error) {
        console.error('Failed to parse checklist from localStorage:', error);
      }
    }
  }, [bookingId]);

  // Save checklist state to localStorage whenever it changes
  useEffect(() => {
    if (!bookingId) {return;}

    const storageKey = `trip-checklist-${bookingId}`;
    localStorage.setItem(storageKey, JSON.stringify(checkedItems));
  }, [checkedItems, bookingId]);

  const toggleItem = (itemId) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Calculate progress percentage
  const completedCount = Object.values(checkedItems).filter(Boolean).length;
  const totalCount = CHECKLIST_ITEMS.length;
  const progressPercentage = Math.round((completedCount / totalCount) * 100);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      {/* Progress Header */}
      <div style={{
        padding: 'var(--space-4)',
        backgroundColor: 'var(--color-background-secondary)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-2)'
        }}>
          <h3 style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-primary)'
          }}>
            Trip Preparation Progress
          </h3>
          <span style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-bold)',
            color: progressPercentage === 100 ? 'var(--color-success)' : 'var(--color-primary)'
          }}>
            {progressPercentage}%
          </span>
        </div>

        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '8px',
          backgroundColor: 'var(--color-border)',
          borderRadius: 'var(--radius-full)',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${progressPercentage}%`,
            backgroundColor: progressPercentage === 100 ? 'var(--color-success)' : 'var(--color-primary)',
            transition: 'width 0.3s ease',
            borderRadius: 'var(--radius-full)'
          }} />
        </div>

        <p style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          marginTop: 'var(--space-2)'
        }}>
          {completedCount} of {totalCount} tasks completed
        </p>
      </div>

      {/* Checklist Items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {CHECKLIST_ITEMS.map((item) => {
          const isChecked = checkedItems[item.id] || false;
          const Icon = item.icon;

          return (
            <div
              key={item.id}
              onClick={() => toggleItem(item.id)}
              style={{
                padding: 'var(--space-4)',
                backgroundColor: isChecked ? 'var(--color-success-light)' : 'var(--color-white)',
                border: `1px solid ${isChecked ? 'var(--color-success)' : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)'
              }}
              onMouseOver={(  _e) => {
                _e.currentTarget.style.borderColor = isChecked ? 'var(--color-success)' : 'var(--color-primary)';
                _e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }}
              onMouseOut={(  _e) => {
                _e.currentTarget.style.borderColor = isChecked ? 'var(--color-success)' : 'var(--color-border)';
                _e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Checkbox Icon */}
              <div style={{ flexShrink: 0, marginTop: '2px' }}>
                {isChecked ? (
                  <CheckCircle2 size={24} color="var(--color-success)" />
                ) : (
                  <Circle size={24} color="var(--color-text-tertiary)" />
                )}
              </div>

              {/* Item Icon */}
              <div style={{
                flexShrink: 0,
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: isChecked ? 'var(--color-success)' : 'var(--color-primary-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Icon size={20} color={isChecked ? 'var(--color-white)' : 'var(--color-primary)'} />
              </div>

              {/* Item Content */}
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-primary)',
                  marginBottom: 'var(--space-1)',
                  textDecoration: isChecked ? 'line-through' : 'none'
                }}>
                  {item.label}
                </div>
                <p style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-secondary)',
                  lineHeight: '1.5'
                }}>
                  {item.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Completion Message */}
      {progressPercentage === 100 && (
        <div style={{
          padding: 'var(--space-4)',
          backgroundColor: 'var(--color-success-light)',
          border: '1px solid var(--color-success)',
          borderRadius: 'var(--radius-md)',
          textAlign: 'center'
        }}>
          <CheckCircle2
            size={32}
            color="var(--color-success)"
            style={{ margin: '0 auto var(--space-2)' }}
          />
          <p style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-success)',
            marginBottom: 'var(--space-1)'
          }}>
            You&apos;re All Set!
          </p>
          <p style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)'
          }}>
            You&apos;ve completed all trip preparation tasks. Have a great adventure!
          </p>
        </div>
      )}
    </div>
  );
}
